"use client";

import { useEffect } from "react";
import { logToSentry } from "../platformDataErrorTracking";
import { dateListResponse } from "@/lib/Types/DateListResponse/DateListResponse";
import { useLocationStore } from "../../stores/locationStore";
import { getIsLoggedIn } from "../../Helpers/userHelpers";
import { PlatformDataEvents } from "../PlatformDataEvents";
import { getPageTrackingData } from "../helpers/getPageTrackingData";
import { channel } from "../../Types/Channel";
import { getAbTestingTrackingData } from "../helpers/getAbTestingTrackingData";
import { getUserTrackingData } from "../helpers/getUserTrackingData";
import { getSearchTrackingData } from "../helpers/getSearchTrackingData";
import { format } from "date-fns";
import { getTravellersFromRooms } from "@/lib/Helpers/urlHelpers";
import { usePlatformDataTracking } from "@/lib/PlatformData/platformDataStore";
import useReviews from "@/app/api/useReviews";
import { AppConfig } from "@/lib/Types/AppConfig";

interface UseDateListTrackingProps {
  appConfig: AppConfig;
  isLoading: boolean;
  dateListData: dateListResponse | undefined;
  searchId: string | null;
  cid: string;
  searchParams: URLSearchParams;
}

export const useDateListTracking = ({
  appConfig,
  isLoading,
  dateListData,
  searchId,
  cid,
  searchParams,
}: UseDateListTrackingProps) => {
  const { pushEvent } = usePlatformDataTracking();
  const { WlConfig, viewMode, userData } = appConfig;
  const location = useLocationStore((state) => state.location);
  const { data: reviewsData } = useReviews({ cid, appConfig });
  const isLoggedIn = getIsLoggedIn();
  const hasNoResults = dateListData?.trackingData?.numberOfResults === 0;

  // Track when results are loaded
  useEffect(() => {
    const hasLocation = !!(
      location.countryName ||
      location.regionName ||
      location.cityName
    );

    if (isLoading || !dateListData || !viewMode || !hasLocation) return;
    const destinationAirportCode =
      dateListData?.dateList[0]?.destinationAirport?.iataCode ?? null;
    const isHotelOnly = viewMode.channel === channel.HOTEL;
    const travellers =
      getTravellersFromRooms(searchParams.get("rooms") || "") ?? [];
    const adults =
      travellers.filter((age: string) => Number(age) >= 25).length ?? 2;
    const children =
      travellers.filter((age: string) => Number(age) >= 2 && Number(age) < 25)
        .length ?? 0;
    const infants =
      travellers.filter((age: string) => Number(age) < 2).length ?? 0;

    const prefix = (() => {
      if (isHotelOnly) return undefined;
      const airports = searchParams.get("departureAirport")?.split(",") ?? [];
      if (airports.length === 0) return "";
      if (airports.length > 1) return `multi${airports.length}`;
      return `${airports[0]}|`;
    })();

    const from = searchParams.get("from");
    const to = searchParams.get("to");
    const departureAirports =
      searchParams.get("departureAirport")?.split(",") ?? [];

    const cityName = location.cityName ?? dateListData?.dateList[0]?.cityName;
    const regionName =
      location.regionName ?? dateListData?.dateList[0]?.regionName;
    const countryName =
      location.countryName ?? dateListData?.dateList[0]?.countryName;

    const tripData = {
      departureDate: from ? format(new Date(from), "yyyyMMdd") : "",
      returnDate: to ? format(new Date(to), "yyyyMMdd") : "",
      ...(departureAirports.length > 0 && {
        outboundOriginCode:
          departureAirports.length === 1 ? departureAirports[0] : "multi",
        outboundOriginName:
          departureAirports.length === 1
            ? dateListData?.dateList[0]?.departureAirport?.name
            : "multi",
      }),
    };

    try {
      pushEvent({
        event: PlatformDataEvents.VISIT,
        transactionAffiliation: "WEGDE",
        profile: viewMode.profile,
        mandator: viewMode.mandator,
        page: getPageTrackingData({
          WlConfig,
          section: "details",
          additionalParams: {
            noResults: hasNoResults ? "filters" : undefined,
          },
        }),
        product: {
          adults,
          children,
          infants,
          category: isHotelOnly ? "to_hotel" : "to_package",
          name: `${isHotelOnly ? "" : prefix}${dateListData?.dateList[0]?.hotelName ?? ""}`,
          sku: `${isHotelOnly ? "" : prefix}${cid}`,
          cheapestPriceInPage: dateListData?.cheapestOffer?.price ?? 0,
          cheapestEstimatedPriceInPage:
            dateListData?.cheapestOffer?.estimatedTotalPrice ??
            dateListData?.cheapestOffer?.totalPrice ??
            0,
          // TODO ask if we can pass unprocessed offers and get rid
          // of transformation to match Sunrise
          //
          // offersList: dateListData?.dateList
          //   ? transformOffersListForTracking(dateListData.dateList)
          //   : {},
          offersList: dateListData?.dateList.reduce(
            (acc: any, offer: any, index: number) => {
              acc[index] = {
                ...offer,
              };
              return acc;
            },
            {}
          ),
          trip: tripData,
          accomodation: {
            internalId: Number(cid),
            isTopSecret: false,
            quantity: 1,
            providerName: searchParams.get("tourOperator") ?? "ALL",
            destinationCode: destinationAirportCode ?? "",
            destinationName: dateListData?.dateList[0]?.cityName || "",
            destinationCountryName: countryName ?? "",
            destinationRegionName: regionName ?? "",
            destinationCityName: cityName ?? "",

            class: String(dateListData?.dateList[0]?.stars),
            reviewsNumber: String(reviewsData?.reviewSummary?.reviewsCount),
            recommendation:
              reviewsData?.reviewSummary?.recommendationRate?.toFixed(1),
            rating: reviewsData?.reviewSummary?.ratingAverage?.toFixed(1),
          },
        },
        search: getSearchTrackingData(
          searchParams,
          isHotelOnly,
          searchId,
          destinationAirportCode
        ),
        user: getUserTrackingData({
          userData,
          isLoggedIn,
          event: PlatformDataEvents.VISIT,
        }),
        ...getAbTestingTrackingData(searchParams),
      });
    } catch (err) {
      logToSentry(err as Error, [
        ["function", "useDateListTracking"],
        ["status", dateListData ? "success" : "fail"],
      ]);
    }
  }, [
    isLoading,
    dateListData,
    WlConfig,
    searchParams,
    pushEvent,
    searchId,
    cid,
    viewMode,
    hasNoResults,
    userData,
    isLoggedIn,
    location,
    reviewsData,
  ]);
};
